import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../common/Icons';
import {
  formatMemberFullName,
  getMemberTypeLabel
} from '../../members/memberListUtils';
import './MemberSelectionTray.css';

function memberInitials(member) {
  const n = (member?.nom || '').trim().charAt(0);
  const p = (member?.prenom || '').trim().charAt(0);
  return (n + p).toUpperCase() || '?';
}

/**
 * Panier de sélection flottant (pattern Gmail / Linear) :
 * barre fixe en bas + tiroir extensible listant les adhérents sélectionnés.
 */
export default function MemberSelectionTray({
  selectedMembers = [],
  isArchiveView = false,
  onRemove,
  onClear,
  onArchive,
  onRestore,
  onDelete
}) {
  const [expanded, setExpanded] = useState(false);
  const count = selectedMembers.length;

  if (count === 0) return null;

  const agentCount = selectedMembers.filter((m) => m.type === 'AGENT').length;
  const foyerHint =
    !isArchiveView && agentCount > 0
      ? `${agentCount} agent${agentCount > 1 ? 's' : ''} — foyers inclus`
      : null;

  const preview = selectedMembers.slice(0, 4);
  const overflow = count - preview.length;

  const primaryLabel = isArchiveView
    ? `Réintégrer ${count} adhérent${count > 1 ? 's' : ''}`
    : `Débaucher ${count} adhérent${count > 1 ? 's' : ''}`;

  const handlePrimary = () => {
    if (isArchiveView) onRestore?.();
    else onArchive?.();
  };

  return createPortal(
    <>
      {expanded && (
        <button
          type="button"
          className="ap-sel-tray__backdrop"
          aria-label="Fermer le panier"
          onClick={() => setExpanded(false)}
        />
      )}

      <div
        className={`ap-sel-tray ${expanded ? 'is-expanded' : ''}`}
        role="region"
        aria-label="Panier de sélection"
        aria-live="polite"
      >
        {expanded && (
          <div className="ap-sel-tray__drawer">
            <header className="ap-sel-tray__drawer-head">
              <h3 className="ap-sel-tray__drawer-title">
                Sélection ({count})
              </h3>
              <button
                type="button"
                className="ap-sel-tray__drawer-close"
                onClick={() => setExpanded(false)}
                aria-label="Réduire le panier"
              >
                <Icon name="chevronDown" size={16} />
              </button>
            </header>
            <ul className="ap-sel-tray__list">
              {selectedMembers.map((member) => {
                const name = formatMemberFullName(member);
                return (
                  <li key={member.id} className="ap-sel-tray__list-item">
                    <span className="ap-sel-tray__chip-avatar" aria-hidden>
                      {memberInitials(member)}
                    </span>
                    <div className="ap-sel-tray__chip-body">
                      <span className="ap-sel-tray__chip-name">{name}</span>
                      <span className="ap-sel-tray__chip-meta">
                        {member.matricule || '—'}
                        {' · '}
                        {getMemberTypeLabel(member.type)}
                        {member.type === 'AGENT' &&
                        Number(member.dependent_count) > 0 &&
                        !isArchiveView
                          ? ` · +${member.dependent_count} ayant(s)-droit`
                          : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="ap-sel-tray__chip-remove"
                      onClick={() => onRemove(member.id)}
                      aria-label={`Retirer ${name} de la sélection`}
                      title="Retirer"
                    >
                      <Icon name="times" size={12} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="ap-sel-tray__bar">
          <div className="ap-sel-tray__bar-left">
            <button
              type="button"
              className="ap-sel-tray__expand"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={expanded ? 'Réduire la sélection' : 'Voir la sélection'}
            >
              <span className="ap-sel-tray__avatars" aria-hidden>
                {preview.map((m, i) => (
                  <span
                    key={m.id}
                    className="ap-sel-tray__avatar"
                    style={{ zIndex: preview.length - i }}
                  >
                    {memberInitials(m)}
                  </span>
                ))}
                {overflow > 0 && (
                  <span className="ap-sel-tray__avatar ap-sel-tray__avatar--more">
                    +{overflow}
                  </span>
                )}
              </span>
              <span className="ap-sel-tray__count">
                <strong>{count}</strong>
                <span>sélectionné{count > 1 ? 's' : ''}</span>
              </span>
              <Icon
                name={expanded ? 'chevronDown' : 'chevronRight'}
                size={14}
                className="ap-sel-tray__chevron"
              />
            </button>
            {foyerHint && (
              <span className="ap-sel-tray__hint">{foyerHint}</span>
            )}
          </div>

          <div className="ap-sel-tray__bar-actions">
            <button
              type="button"
              className="ap-sel-tray__btn ap-sel-tray__btn--ghost"
              onClick={onClear}
            >
              Tout effacer
            </button>
            {isArchiveView ? (
              <>
                <button
                  type="button"
                  className="ap-sel-tray__btn ap-sel-tray__btn--primary"
                  onClick={() => onRestore?.()}
                >
                  <Icon name="userPlus" size={15} />
                  <span>{primaryLabel}</span>
                </button>
                <button
                  type="button"
                  className="ap-sel-tray__btn ap-sel-tray__btn--danger"
                  onClick={() => onDelete?.()}
                >
                  <Icon name="trash" size={15} />
                  <span>
                    Supprimer {count} adhérent{count > 1 ? 's' : ''}
                  </span>
                </button>
              </>
            ) : (
              <button
                type="button"
                className="ap-sel-tray__btn ap-sel-tray__btn--danger"
                onClick={handlePrimary}
              >
                <Icon name="inbox" size={15} />
                <span>{primaryLabel}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
